import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Autocomplete, Box, CircularProgress, MenuItem,
  Select, TextField, Typography,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { makeStyles } from "tss-react/mui";
import SearchIcon from "@mui/icons-material/Search";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import i18next from "i18next";
import { BRAND, FONT, RADIUS, useLightStyles } from "../lightTheme";
import { Context } from "states/Store";
import { RoleBasedAccessControlContext, PrivilegeEnum } from "services/common/RolesUtils";
import { searchThirdparty, createUnmanagedThirdparty } from "services/edge/ThirdpartyService";
import { getAccountAttributesByBondType } from "services/edge/AttributesService";
import { getCountries, getDividedCountries, formatIdentifier, hasOption, formatActivityTextFromActivity } from "services/common/Utils";
import { getDefaultShownValuesIfExist, getDefaultValuesIfExist } from "components/thirdparties/unmanagedThirdParty/UnmanagedThirdPartyUtils";
import LightAttributesForm from "./LightAttributesForm";

// ─── Searchable select (reusable) ─────────────────────────────────────────────
function SearchSelect({ options, value, onChange, placeholder, noOptionsText = i18next.t("light.common.noResult") }) {
  const selected = options.find((o) => o.value === value) ?? null;
  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      onChange={(_, opt) => onChange(opt?.value ?? "")}
      getOptionLabel={(o) => o.label ?? ""}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      noOptionsText={noOptionsText}
      disableClearable
      renderInput={(params) => <TextField {...params} placeholder={placeholder} />}
      renderOption={(props, option) => (
        <li {...props} key={option.value} style={{ fontFamily: "Inter, sans-serif", fontSize: "0.82rem", padding: "6px 12px" }}>
          {option.label}
        </li>
      )}
      sx={{
        "& .MuiInputBase-root": {
          height: "36px", borderRadius: "8px", fontFamily: "Inter, sans-serif",
          fontSize: "0.82rem", backgroundColor: "#fff",
          "& fieldset": { borderWidth: "1.5px", borderColor: BRAND.border },
          "&:hover fieldset": { borderColor: BRAND.primary },
          "&.Mui-focused fieldset": { borderColor: BRAND.primary, borderWidth: "1.5px" },
        },
        "& .MuiInputBase-input": { padding: "6px 10px !important", fontFamily: "Inter, sans-serif", fontSize: "0.82rem" },
        "& .MuiAutocomplete-popupIndicator": { padding: "2px" },
      }}
      slotProps={{
        paper: {
          sx: {
            fontFamily: "Inter, sans-serif", fontSize: "0.82rem",
            borderRadius: "10px", border: `1px solid ${BRAND.border}`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.10)", mt: "4px",
          },
        },
        listbox: { sx: { maxHeight: 220, overflow: "auto" } },
        popper: { sx: { zIndex: 1400 } },
      }}
    />
  );
}

const SEARCH_BY_OPTIONS = [
  { value: "NAME",     label: () => i18next.t("light.launch.socialName") },
  { value: "LOCAL_ID", label: () => i18next.t("light.launch.localId") },
  { value: "DUNS",     label: () => "DUNS" },
  { value: "VAT",      label: () => i18next.t("light.launch.vatNumber") },
];

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(16px); }
  100% { opacity: 1; transform: translateY(0); }
`;
const popIn = keyframes`
  0% { opacity: 0; transform: scale(0.5); }
  60% { opacity: 1; transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
`;
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const useStyles = makeStyles()(() => ({
  searchCard: {
    marginBottom: "1.5rem",
  },

  // ── Empty state ──
  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "2rem 0", textAlign: "center",
    animation: `${fadeIn} 0.5s ease-out`,
    width: "100%",
  },
  emptyTable: {
    width: "100%", maxWidth: "1100px",
    margin: "0 auto",
    borderRadius: "14px", overflow: "hidden",
    border: `1.5px solid ${BRAND.border}`,
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
    backgroundColor: BRAND.white,
    marginTop: "1.5rem",
  },
  emptyTableHead: {
    display: "flex", padding: "0.55rem 1rem",
    backgroundColor: BRAND.background,
    borderBottom: `1px solid ${BRAND.border}`,
    fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.08em", color: BRAND.textMuted, fontFamily: "Inter, sans-serif",
    "@media (max-width: 599px)": { display: "none" },
  },
  emptyTableRow: {
    display: "flex", alignItems: "center",
    padding: "0.6rem 1rem",
    borderBottom: `1px solid ${BRAND.border}`,
    opacity: 0, animation: `${fadeIn} 0.4s ease-out forwards`,
    "@media (max-width: 599px)": {
      padding: "0.7rem 0.9rem", gap: "0.6rem",
      "& > :nth-of-type(1)": { flex: "1 1 auto !important", minWidth: 0 },
      "& > :nth-of-type(2)": { flex: "0 0 auto !important", textAlign: "right" },
      "& > :nth-of-type(3)": { display: "none !important" },
    },
  },
  emptyTableShimmer: {
    height: "6px", borderRadius: "3px",
    background: `linear-gradient(90deg, #E5E7EB 0%, #F3F4F6 50%, #E5E7EB 100%)`,
    backgroundSize: "200px 100%",
    animation: `${shimmer} 2s linear infinite`,
  },
  emptyTablePill: {
    display: "inline-flex", alignItems: "center",
    padding: "2px 7px", borderRadius: "10px",
    fontSize: "0.6rem", fontWeight: 600, fontFamily: "Inter, sans-serif",
  },
  emptyTableNewRow: {
    display: "flex", alignItems: "center",
    padding: "0.7rem 1rem",
    backgroundColor: `${BRAND.secondary}08`,
    borderTop: `2px solid ${BRAND.secondary}`,
    opacity: 0, animation: `${popIn} 0.5s ease-out 0.8s forwards`,
    "@media (max-width: 599px)": {
      padding: "0.75rem 0.9rem", gap: "0.6rem",
      "& > :nth-of-type(1)": { flex: "1 1 auto !important", minWidth: 0 },
      "& > :nth-of-type(2)": { display: "none !important" },
      "& > :nth-of-type(3)": { flex: "0 0 auto !important", gap: "4px !important" },
    },
  },
  emptyNewBadge: {
    display: "inline-flex", alignItems: "center",
    padding: "2px 7px", borderRadius: "10px",
    fontSize: "0.6rem", fontWeight: 700, fontFamily: "Inter, sans-serif",
    backgroundColor: `${BRAND.secondary}20`, color: BRAND.primary,
  },
  emptyNewCheck: {
    width: "18px", height: "18px", borderRadius: "50%",
    backgroundColor: "#DCFCE7", border: "1.5px solid #15803D",
    display: "flex", alignItems: "center", justifyContent: "center",
    opacity: 0, animation: `${popIn} 0.3s ease-out 1.5s forwards`,
    "@media (max-width: 599px)": { display: "none" },
  },
  emptyTitle: {
    fontSize: "1.05rem", fontWeight: 700, color: BRAND.textMain,
    fontFamily: "Inter, sans-serif", marginBottom: "0.35rem",
  },
  emptyDesc: {
    fontSize: "0.82rem", color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif", lineHeight: 1.6, maxWidth: "400px",
    margin: "0 auto",
  },
  searchForm: {
    display: "flex",
    gap: "0.75rem",
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
    flex: "1 1 160px",
    minWidth: 0,
    "@media (max-width: 599px)": {
      flex: "1 1 100% !important",
      width: "100%",
    },
  },
  fieldLabel: {
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
  },
  searchInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      fontFamily: "Inter, sans-serif",
      fontSize: "0.875rem",
      backgroundColor: BRAND.white,
      "& fieldset": { borderColor: BRAND.border },
      "&:hover fieldset": { borderColor: BRAND.primary },
      "&.Mui-focused fieldset": { borderColor: BRAND.primary },
    },
  },
  searchSelect: {
    borderRadius: "10px !important",
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    backgroundColor: BRAND.white,
    "& fieldset": { borderColor: BRAND.border },
    "&:hover fieldset": { borderColor: BRAND.primary },
    "&.Mui-focused fieldset": { borderColor: BRAND.primary },
  },
  searchBtnWrap: {
    paddingBottom: "1px",
    "@media (max-width: 599px)": {
      flex: "1 1 100%",
      width: "100%",
    },
  },
  searchBtn: {
    height: "40px",
    minWidth: "130px",
    backgroundColor: BRAND.primary,
    color: BRAND.white,
    border: "none",
    borderRadius: "10px",
    padding: "0 1.25rem",
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    flexShrink: 0,
    "@media (max-width: 599px)": { width: "100%", justifyContent: "center" },
    transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  // Results
  resultsList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
    marginTop: "0.5rem",
  },
  resultCard: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    padding: "1rem 1.25rem",
    transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(0,58,34,0.10)",
      borderColor: BRAND.primary,
    },
    "&:hover $selectBtn": { opacity: 1 },
    "@media (max-width: 599px)": {
      flexWrap: "wrap", alignItems: "flex-start", gap: "0.65rem",
      padding: "0.85rem 0.9rem",
    },
  },
  resultInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
    "@media (max-width: 599px)": {
      flexBasis: "calc(100% - 56px)",
    },
  },
  resultName: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "@media (max-width: 599px)": {
      whiteSpace: "normal", overflow: "visible", textOverflow: "clip",
      wordBreak: "break-word",
    },
  },
  resultMeta: {
    fontSize: "0.75rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    "@media (max-width: 599px)": { wordBreak: "break-word" },
  },
  onboardedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    flexShrink: 0,
    whiteSpace: "nowrap",
    backgroundColor: BRAND.tertiary,
    color: BRAND.primary,
    borderRadius: "20px",
    padding: "2px 8px",
    fontSize: "0.7rem",
    fontWeight: 600,
    fontFamily: "Inter, sans-serif",
  },
  selectBtn: {
    height: "36px",
    borderRadius: "8px",
    backgroundColor: BRAND.white,
    color: BRAND.primary,
    fontFamily: "Inter, sans-serif",
    fontSize: "0.8rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
    opacity: 0,
    width: 0, padding: 0, border: "none", overflow: "hidden", flexShrink: 0,
    transition: "all 0.15s",
    "$resultCard:hover &": { opacity: 1, width: "auto", padding: "0 1rem", border: `1.5px solid ${BRAND.primary}` },
    "&:hover": { backgroundColor: BRAND.primary, color: BRAND.white },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
    // Touch devices: show the button permanently since hover is unreliable
    "@media (hover: none)": {
      opacity: 1, width: "auto", padding: "0 1rem",
      border: `1.5px solid ${BRAND.primary}`,
    },
  },
  resultsLabel: {
    fontSize: "0.75rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    marginBottom: "0.75rem",
  },
  attrHeader: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "1.25rem",
  },
  attrTitle: {
    fontSize: "1rem",
    fontWeight: 700,
    color: BRAND.textMain,
    fontFamily: "Inter, sans-serif",
  },
  attrSubtitle: {
    fontSize: "0.8rem",
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    marginTop: "0.15rem",
  },
  attrActions: {
    display: "flex",
    gap: "0.75rem",
    marginTop: "1.5rem",
    justifyContent: "space-between",
    width: "100%",
  },
  backBtn: {
    height: "40px",
    padding: "0 1.25rem",
    borderRadius: "10px",
    border: `1.5px solid ${BRAND.border}`,
    backgroundColor: BRAND.white,
    color: BRAND.textMuted,
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    transition: "border-color 0.15s, color 0.15s",
    "&:hover": { borderColor: BRAND.primary, color: BRAND.primary },
  },
  continueBtn: {
    height: "40px",
    padding: "0 1.5rem",
    borderRadius: "10px",
    border: "none",
    backgroundColor: BRAND.primary,
    color: BRAND.white,
    fontFamily: "Inter, sans-serif",
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
  },
}));

export default function StepChooseTP({ onSelect, reopenAttributes, stepper }) {
  const { classes: s, cx } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = React.useContext(RoleBasedAccessControlContext);
  const canCreateUnmanaged = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_UNMANAGED);

  // Countries — initialized synchronously so the Select shows "France" on first render
  const [favoriteCountries] = useState(() => getDividedCountries(getCountries())[0]);
  const [otherCountries] = useState(() => getDividedCountries(getCountries())[1]);
  const [isDnbOrInternational, setIsDnbOrInternational] = useState(false);

  // Form
  const [selectedCountry, setSelectedCountry] = useState("FR");
  const [searchBy, setSearchBy] = useState("NAME");
  const [searchValue, setSearchValue] = useState("");

  // Results
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);

  // Add TP loading
  const [addingId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10);

  // Attributes inline form
  const [accountAttributes, setAccountAttributes] = useState([]);
  const [attributesOpen, setAttributesOpen] = useState(false);
  const [pendingRow, setPendingRow] = useState(null);
  const attrFormRef = useRef(null);

  // Unmanaged TP creation
  // Unmanaged TP creation
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", activity: "", legalCategory: "", capital: "", capitalCurrency: "EUR", category: "", identifier: "", headquarter: "true", country: "FR", postCode: "", city: "", address: "" });
  const [creating, setCreating] = useState(false);

  // Activity & legal category options from global store
  const activityOptions = React.useMemo(() =>
    (state.activityTraductions ?? [])
      .filter((a) => a.origine === "DNB")
      .sort((a, b) => formatActivityTextFromActivity(a).localeCompare(formatActivityTextFromActivity(b)))
      .map((a) => ({ value: a.code, label: formatActivityTextFromActivity(a) })),
    [state.activityTraductions]
  );
  const legalCategoryOptions = React.useMemo(() =>
    Object.entries(state.legalActivityTraductions ?? {}).map(([key, label]) => ({ value: key, label })),
    [state.legalActivityTraductions]
  );

  // Reopen attributes form when coming back from step 2, restoring saved values
  useEffect(() => {
    if (reopenAttributes && accountAttributes.length > 0) {
      setPendingRow({
        name: reopenAttributes.name,
        identifier: reopenAttributes.identifier,
        id: reopenAttributes.rawId,
        isIdEncrypted: reopenAttributes.isIdEncrypted,
        addressCity: reopenAttributes.city,
        addressPostCode: reopenAttributes.postCode,
      });
      // Restore saved attribute values into accountAttributes
      if (reopenAttributes.pendingAttributes?.length > 0) {
        const saved = reopenAttributes.pendingAttributes;
        setAccountAttributes((prev) => prev.map((attr) => {
          const match = saved.find((s) => s.attributeId === attr.attributeId || s.id === attr.id);
          return match ? { ...attr, values: match.values, shownValues: match.shownValues } : attr;
        }));
      }
      setAttributesOpen(true);
    }
  }, [reopenAttributes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load options
  useEffect(() => {
    setIsDnbOrInternational(
      hasOption(state.accounts, state.account?.id, "DNB") ||
      hasOption(state.accounts, state.account?.id, "INTERNATIONAL")
    );
  }, [state.accounts, state.account]);

  // Load attributes
  useEffect(() => {
    if (!state.account) return;
    (async () => {
      try {
        const res = await getAccountAttributesByBondType(state.account.id, "thirdparty", null);
        setAccountAttributes(
          res.content.map((a) => ({
            ...a,
            id: a.label,
            shownValues: getDefaultShownValuesIfExist(a),
            values: getDefaultValuesIfExist(a),
            attributeId: a.id,
          })).sort((a, b) => a.label.localeCompare(b.label))
        );
      } catch (_) {}
    })();
  }, [state.account]);

  const allCountries = [...favoriteCountries, ...otherCountries];

  const handleSearch = async () => {
    if (!searchValue.trim()) return;
    setSearching(true);
    setSearched(true);
    setResults([]);
    setVisibleCount(10);
    try {
      // Auto-detect SIREN (9 digits) / SIRET (14 digits)
      let effectiveSearchBy = searchBy;
      if (searchBy === "LOCAL_ID") {
        const digits = searchValue.trim().replace(/\s/g, "");
        if (/^\d{9}$/.test(digits)) effectiveSearchBy = "SIREN";
        else if (/^\d{14}$/.test(digits)) effectiveSearchBy = "SIRET";
      }
      let res = await searchThirdparty(state.account.id, {
        countryCode: selectedCountry,
        searchBy: effectiveSearchBy,
        value: searchValue.trim(),
      }, dispatch);
      res = (res ?? []).map((r) => ({
        ...r,
        identifier: formatIdentifier(r, selectedCountry, searchBy, searchValue.trim()),
      }));
      setResults(res);
    } catch (_) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelect = useCallback(async (row) => {
    // Already onboarded → select directly
    if (row.onboarded && row.thirdPartyId) {
      onSelect?.({ thirdpartyId: row.thirdPartyId, name: row.name, identifier: row.identifier, city: row.addressCity, postCode: row.addressPostCode, onboarded: true });
      return;
    }
    // Not onboarded: collect attributes if needed, but don't add TP yet
    if (accountAttributes.length > 0) {
      setPendingRow(row);
      setAttributesOpen(true);
      return;
    }
    selectPending(row, []);
  }, [accountAttributes, onSelect]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateUnmanaged = async () => {
    if (!createForm.name.trim() || !createForm.city.trim() || !createForm.postCode.trim()) return;
    setCreating(true);
    try {
      const unmanagedInfo = {
        name: createForm.name.trim(),
        etabActivityCode: createForm.activity || undefined,
        legalCategoryCode: createForm.legalCategory || undefined,
        capital: createForm.capital ? Number(createForm.capital) : undefined,
        capitalCurrency: createForm.capital ? createForm.capitalCurrency : undefined,
        category: createForm.category || undefined,
        companyIdValue: createForm.identifier.trim() || undefined,
        headquarter: createForm.headquarter === "true",
        addressCountryCode: createForm.country,
        addressPostCode: createForm.postCode.trim(),
        addressCity: createForm.city.trim(),
        addressStreet: createForm.address.trim() || undefined,
      };
      const attrs = accountAttributes.map((a) => ({
        label: a.label,
        type: a.type,
        values: a.values ?? [],
        id: a.attributeId,
      }));
      const res = await createUnmanagedThirdparty(state.account.id, unmanagedInfo, attrs, dispatch);
      const tp = res?.content;
      if (tp) {
        onSelect?.({
          thirdpartyId: tp.thirdpartyId ?? tp.id,
          name: createForm.name.trim(),
          identifier: createForm.identifier.trim() || "",
          city: createForm.city.trim(),
          postCode: createForm.postCode.trim(),
          onboarded: true,
        });
      }
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: "ERROR", msg: e?.message || i18next.t("light.launch.createError") } });
    } finally {
      setCreating(false);
    }
  };

  const selectPending = (row, attrs) => {
    onSelect?.({
      name: row.name,
      identifier: row.identifier,
      city: row.addressCity,
      postCode: row.addressPostCode,
      onboarded: false,
      rawId: row.id,
      isIdEncrypted: row.isIdEncrypted,
      pendingAttributes: attrs,
    });
  };

  return (
    <Box className={light.pageRoot}>
      <Box className={light.pageHeader}>
        <Box className={light.pageHeaderInner} style={{ maxWidth: "1100px" }}>
          <Typography className={light.pageTitleWhite}>{i18next.t("light.launch.searchTitle")}</Typography>
          <Typography className={light.pageSubtitleWhite}>
            {i18next.t("light.launch.searchSubtitle")}
          </Typography>
        </Box>
      </Box>
      <Box className={light.pageInner} style={{ maxWidth: "1100px", marginTop: "-0.75rem" }}>
        <Box className={light.card} style={{ overflow: "hidden" }}>
          {stepper}
          <Box style={{ padding: "1.25rem" }}>
          {/* Search form */}
          <Box className={s.searchCard}>
          <Box className={s.searchForm}>
            {/* Country — disabled if no DNB/INTERNATIONAL subscription */}
            <Box className={s.fieldWrap} style={{ flex: "0 1 220px" }}>
              <Typography className={s.fieldLabel}>{i18next.t("country")}</Typography>
              {isDnbOrInternational ? (
                <SearchSelect
                  options={allCountries.map((c) => ({ value: c.key, label: c.label }))}
                  value={selectedCountry}
                  onChange={(v) => setSelectedCountry(v || "FR")}
                  placeholder={i18next.t("country")}
                />
              ) : (
                <input
                  className={light.fieldInput}
                  style={{ width: "100%", boxSizing: "border-box" }}
                  value="France"
                  disabled
                />
              )}
            </Box>

            {/* Search by — hide DUNS if no DNB/INTERNATIONAL */}
            <Box className={s.fieldWrap} style={{ flex: "0 1 180px" }}>
              <Typography className={s.fieldLabel}>{i18next.t("light.launch.searchBy")}</Typography>
              <Select
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
                size="small"
                className={s.searchSelect}
              >
                {SEARCH_BY_OPTIONS.filter((o) => isDnbOrInternational || o.value !== "DUNS").map((o) => (
                  <MenuItem key={o.value} value={o.value} style={{ fontFamily: "Inter", fontSize: "0.875rem" }}>
                    {o.label()}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Value */}
            <Box className={s.fieldWrap} style={{ flex: "1 1 280px" }}>
              <Typography className={s.fieldLabel}>{i18next.t("light.launch.value")}</Typography>
              <TextField
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder={i18next.t("light.launch.searchPlaceholder")}
                size="small"
                className={s.searchInput}
              />
            </Box>

            {/* Search button */}
            <Box className={s.searchBtnWrap}>
              <button
                className={s.searchBtn}
                onClick={handleSearch}
                disabled={!searchValue.trim() || searching}
              >
                {searching
                  ? <CircularProgress size={14} style={{ color: BRAND.white }} />
                  : <SearchIcon style={{ fontSize: "16px" }} />
                }
                {i18next.t("search")}
              </button>
            </Box>
          </Box>
        </Box>

        {/* Empty state — before any search */}
        {!attributesOpen && !searched && (
          <Box className={s.emptyState}>
            <Typography className={s.emptyTitle}>{i18next.t("light.launch.emptyTitle")}</Typography>
            <Typography className={s.emptyDesc}>
              {i18next.t("light.launch.emptyDesc")}
            </Typography>

            {/* Mini animated table */}
            <Box className={s.emptyTable}>
              {/* Header */}
              <Box className={s.emptyTableHead}>
                <span style={{ flex: 2 }}>{i18next.t("light.common.supplier")}</span>
                <span style={{ flex: 1.5 }}>{i18next.t("light.common.evaluation")}</span>
                <span style={{ flex: 1 }}></span>
              </Box>
              {/* Existing rows */}
              {[
                { name: i18next.t("light.launch.exampleTpA"), eval: i18next.t("light.launch.exampleEvalA"), delay: "0.1s" },
                { name: i18next.t("light.launch.exampleTpB"), eval: i18next.t("light.launch.exampleEvalB"), delay: "0.2s" },
              ].map((row, i) => (
                <Box key={i} className={s.emptyTableRow} style={{ animationDelay: row.delay }}>
                  <Box style={{ flex: 2, display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box style={{ width: "22px", height: "22px", borderRadius: "6px", backgroundColor: `${BRAND.primary}10`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <BusinessOutlinedIcon style={{ fontSize: "11px", color: BRAND.primary, opacity: 0.5 }} />
                    </Box>
                    <Box className={s.emptyTableShimmer} style={{ width: "70%" }} />
                  </Box>
                  <Box style={{ flex: 1.5 }}>
                    <Typography style={{ fontSize: "0.68rem", color: BRAND.textMuted, fontFamily: "Inter", opacity: 0.7 }}>{row.eval}</Typography>
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Box className={s.emptyTableShimmer} style={{ width: "50%" }} />
                  </Box>
                </Box>
              ))}
              {/* NEW row — pops in */}
              <Box className={s.emptyTableNewRow}>
                <Box style={{ flex: 2, display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box style={{ width: "22px", height: "22px", borderRadius: "6px", backgroundColor: `${BRAND.secondary}25`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <BusinessOutlinedIcon style={{ fontSize: "11px", color: BRAND.primary }} />
                  </Box>
                  <Typography style={{ fontSize: "0.72rem", fontWeight: 700, color: BRAND.textMain, fontFamily: "Inter" }}>{i18next.t("light.launch.exampleYourTp")}</Typography>
                </Box>
                <Box style={{ flex: 1.5 }}>
                  <Typography style={{ fontSize: "0.68rem", fontWeight: 600, color: BRAND.primary, fontFamily: "Inter" }}>{i18next.t("light.launch.exampleEvalC")}</Typography>
                </Box>
                <Box style={{ flex: 1, display: "flex", alignItems: "center", gap: "6px" }}>
                  <span className={s.emptyNewBadge}>+ {i18next.t("light.launch.new")}</span>
                  <Box className={s.emptyNewCheck}>
                    <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2 2 4-4" stroke="#15803D" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
                  </Box>
                </Box>
              </Box>
            </Box>

          </Box>
        )}

        {/* Results */}
        {!attributesOpen && searched && (
          <>
            {searching ? (
              <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.6rem", padding: "2.5rem 0" }}>
                <CircularProgress size={18} style={{ color: BRAND.primary }} />
                <Typography style={{ fontSize: "0.85rem", color: BRAND.textMuted, fontFamily: "Inter" }}>{i18next.t("light.common.loading")}</Typography>
              </Box>
            ) : (
            <>
            <Typography className={s.resultsLabel}>
              {results.length > 0
                ? i18next.t("light.launch.resultsFound", { count: results.length })
                : i18next.t("light.launch.noResults")}
            </Typography>

            <Box className={s.resultsList}>
              {results.slice(0, visibleCount).map((row) => (
                <Box
                  key={row.id ?? row.name}
                  className={cx(light.card, s.resultCard)}
                  onClick={() => !addingId && handleSelect(row)}
                >
                  {/* Info */}
                  <Box className={s.resultInfo}>
                    <Typography className={s.resultName}>{row.name}</Typography>
                    <Typography className={s.resultMeta}>
                      {[row.identifier, row.addressPostCode, row.addressCity].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>

                  {/* Bottom row: badge left, select button right */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem", flexShrink: 0, marginLeft: { xs: 0, sm: "auto" }, flexWrap: "wrap", width: { xs: "100%", sm: "auto" }, justifyContent: { xs: "space-between", sm: "flex-end" } }}>
                  {row.onboarded ? (
                    <Box className={s.onboardedBadge}>
                      <CheckCircleOutlineIcon style={{ fontSize: "12px" }} />
                      {i18next.t("light.launch.inPortfolio")}
                    </Box>
                  ) : <span />}
                  <button
                    className={s.selectBtn}
                    disabled={!!addingId}
                    onClick={(e) => { e.stopPropagation(); handleSelect(row); }}
                  >
                    {addingId === row.id
                      ? <CircularProgress size={12} style={{ color: BRAND.primary }} />
                      : row.onboarded
                        ? <CheckCircleOutlineIcon style={{ fontSize: "14px" }} />
                        : <AddCircleOutlineIcon style={{ fontSize: "14px" }} />
                    }
                    {row.onboarded ? i18next.t("light.launch.select") : i18next.t("add")}
                  </button>
                  </Box>
                </Box>
              ))}
            </Box>

            {visibleCount < results.length && (
              <Box style={{ textAlign: "center", marginTop: "1rem" }}>
                <button
                  className={s.selectBtn}
                  style={{ margin: "0 auto", padding: "0 1.5rem", height: "40px", borderColor: BRAND.border, color: BRAND.textMuted }}
                  onClick={() => setVisibleCount((n) => n + 10)}
                >
                  {i18next.t("light.launch.showMore", { count: results.length - visibleCount })}
                </button>
              </Box>
            )}

            {/* Create unmanaged TP — only if user has privilege */}
            {canCreateUnmanaged && !showCreateForm && results.length === 0 && (
              <Box style={{ textAlign: "center", marginTop: "1.5rem", paddingTop: "1rem", borderTop: `1px solid ${BRAND.border}` }}>
                <Typography style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.5rem" }}>
                  {results.length === 0 ? i18next.t("light.launch.notFound") : i18next.t("light.launch.notInResults")}
                </Typography>
                <button
                  onClick={() => { setShowCreateForm(true); setCreateForm((f) => ({ ...f, name: searchValue.trim(), country: selectedCountry })); }}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "0.4rem",
                    padding: "0.5rem 1.25rem", borderRadius: RADIUS.md,
                    border: `1.5px solid ${BRAND.primary}`, backgroundColor: BRAND.white,
                    color: BRAND.primary, ...FONT.h3, fontFamily: FONT.family, cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${BRAND.primary}08`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = BRAND.white; }}
                >
                  <PersonAddOutlinedIcon style={{ fontSize: "16px" }} />
                  {i18next.t("light.launch.createManually")}
                </button>
              </Box>
            )}
            {canCreateUnmanaged && showCreateForm && (
              <Box className={light.card} style={{ marginTop: "1.5rem" }}>
                <Box style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
                  <Box className={light.iconWrap} style={{ backgroundColor: `${BRAND.primary}10` }}>
                    <BusinessOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary }} />
                  </Box>
                  <Box>
                    <Typography style={{ ...FONT.h3, color: BRAND.textMain, fontFamily: FONT.family }}>{i18next.t("light.unmanaged.title")}</Typography>
                    <Typography style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family }}>{i18next.t("light.unmanaged.subtitle")}</Typography>
                  </Box>
                </Box>

                {/* Section: Informations du tiers */}
                <Typography style={{ ...FONT.label, color: BRAND.primary, fontFamily: FONT.family, marginBottom: "0.75rem" }}>{i18next.t("light.unmanaged.tpInfo")}</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "0.75rem 1rem", marginBottom: "1.25rem" }}>
                  <Box>
                    <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("corporateName")} *</Typography>
                    <input
                      className={light.fieldInput}
                      style={{ width: "100%", boxSizing: "border-box" }}
                      placeholder={i18next.t("light.unmanaged.namePlaceholder")}
                      value={createForm.name}
                      onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                      autoFocus
                    />
                  </Box>
                  <Box>
                    <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("light.unmanaged.chooseActivity")} *</Typography>
                    <SearchSelect
                      options={activityOptions}
                      value={createForm.activity}
                      onChange={(v) => setCreateForm((f) => ({ ...f, activity: v }))}
                      placeholder={i18next.t("light.unmanaged.chooseActivity")}
                    />
                  </Box>
                  <Box>
                    <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("legalCategory")}</Typography>
                    <SearchSelect
                      options={legalCategoryOptions}
                      value={createForm.legalCategory}
                      onChange={(v) => setCreateForm((f) => ({ ...f, legalCategory: v }))}
                      placeholder={i18next.t("light.unmanaged.chooseLegalCategory")}
                    />
                  </Box>
                  <Box>
                    <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("capital")}</Typography>
                    <Box style={{ display: "flex", gap: "0.5rem" }}>
                      <input
                        className={light.fieldInput}
                        style={{ flex: 1, boxSizing: "border-box" }}
                        placeholder="…€"
                        type="number"
                        value={createForm.capital}
                        onChange={(e) => setCreateForm((f) => ({ ...f, capital: e.target.value }))}
                      />
                      <select
                        className={light.fieldSelect}
                        style={{ width: "80px", boxSizing: "border-box" }}
                        value={createForm.capitalCurrency}
                        onChange={(e) => setCreateForm((f) => ({ ...f, capitalCurrency: e.target.value }))}
                      >
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                        <option value="CHF">CHF</option>
                      </select>
                    </Box>
                  </Box>
                  <Box>
                    <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("unmanagedThirdParty.label.category")}</Typography>
                    <select
                      className={light.fieldSelect}
                      style={{ width: "100%", boxSizing: "border-box" }}
                      value={createForm.category}
                      onChange={(e) => setCreateForm((f) => ({ ...f, category: e.target.value }))}
                    >
                      <option value="">{i18next.t("light.unmanaged.chooseCategory")}</option>
                      <option value="PME">PME</option>
                      <option value="ETI">ETI</option>
                      <option value="GE">Grande Entreprise</option>
                      <option value="AUT">Autre</option>
                    </select>
                  </Box>
                  <Box>
                    <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("unmanagedThirdParty.label.id_local")}</Typography>
                    <input
                      className={light.fieldInput}
                      style={{ width: "100%", boxSizing: "border-box" }}
                      placeholder={i18next.t("unmanagedThirdParty.label.id_local")}
                      value={createForm.identifier}
                      onChange={(e) => setCreateForm((f) => ({ ...f, identifier: e.target.value }))}
                    />
                  </Box>
                  <Box style={{ gridColumn: "1 / -1" }}>
                    <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.35rem" }}>{i18next.t("unmanagedThirdParty.label.etabType")}</Typography>
                    <Box style={{ display: "flex", gap: "1.5rem" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", ...FONT.body, fontFamily: FONT.family, color: BRAND.textMain, cursor: "pointer" }}>
                        <input type="radio" name="headquarter" value="true" checked={createForm.headquarter === "true"} onChange={(e) => setCreateForm((f) => ({ ...f, headquarter: e.target.value }))} />
                        {i18next.t("light.unmanaged.headOffice")}
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", ...FONT.body, fontFamily: FONT.family, color: BRAND.textMain, cursor: "pointer" }}>
                        <input type="radio" name="headquarter" value="false" checked={createForm.headquarter === "false"} onChange={(e) => setCreateForm((f) => ({ ...f, headquarter: e.target.value }))} />
                        {i18next.t("light.unmanaged.secondary")}
                      </label>
                    </Box>
                  </Box>
                </Box>

                {/* Section: Coordonnées */}
                <Box style={{ borderTop: `1px solid ${BRAND.border}`, paddingTop: "1.25rem" }}>
                  <Typography style={{ ...FONT.label, color: BRAND.primary, fontFamily: FONT.family, marginBottom: "0.75rem" }}>{i18next.t("light.unmanaged.coordinates")}</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "0.75rem 1rem" }}>
                    <Box>
                      <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("country")} *</Typography>
                      <SearchSelect
                        options={[...favoriteCountries, ...otherCountries].map((c) => ({ value: c.key, label: c.label }))}
                        value={createForm.country}
                        onChange={(v) => setCreateForm((f) => ({ ...f, country: v || "FR" }))}
                        placeholder={i18next.t("country")}
                      />
                    </Box>
                    <Box>
                      <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("postalCode")} *</Typography>
                      <input
                        className={light.fieldInput}
                        style={{ width: "100%", boxSizing: "border-box" }}
                        placeholder={i18next.t("postalCode")}
                        value={createForm.postCode}
                        onChange={(e) => setCreateForm((f) => ({ ...f, postCode: e.target.value }))}
                      />
                    </Box>
                    <Box>
                      <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("city")} *</Typography>
                      <input
                        className={light.fieldInput}
                        style={{ width: "100%", boxSizing: "border-box" }}
                        placeholder={i18next.t("city")}
                        value={createForm.city}
                        onChange={(e) => setCreateForm((f) => ({ ...f, city: e.target.value }))}
                      />
                    </Box>
                    <Box>
                      <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>{i18next.t("address")}</Typography>
                      <input
                        className={light.fieldInput}
                        style={{ width: "100%", boxSizing: "border-box" }}
                        placeholder={i18next.t("address")}
                        value={createForm.address}
                        onChange={(e) => setCreateForm((f) => ({ ...f, address: e.target.value }))}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginTop: "1.5rem", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    style={{
                      padding: "0.5rem 1.25rem", borderRadius: RADIUS.md,
                      border: `1px solid ${BRAND.border}`, backgroundColor: BRAND.white,
                      color: BRAND.textMuted, ...FONT.body, fontFamily: FONT.family, cursor: "pointer",
                    }}
                  >
                    {i18next.t("cancel")}
                  </button>
                  <button
                    onClick={handleCreateUnmanaged}
                    disabled={creating || !createForm.name.trim() || !createForm.activity || !createForm.city.trim() || !createForm.postCode.trim()}
                    style={{
                      padding: "0.5rem 1.5rem", borderRadius: RADIUS.md,
                      border: "none", backgroundColor: BRAND.primary, color: BRAND.white,
                      ...FONT.h3, fontFamily: FONT.family, cursor: "pointer",
                      opacity: (!createForm.name.trim() || !createForm.activity || !createForm.city.trim() || !createForm.postCode.trim()) ? 0.5 : 1,
                    }}
                  >
                    {creating ? <CircularProgress size={14} style={{ color: BRAND.white }} /> : i18next.t("light.unmanaged.createAndContinue")}
                  </button>
                </Box>
              </Box>
            )}
          </>
            )}
          </>
        )}

        {/* Inline attributes form (for new TPs) */}
        {attributesOpen && pendingRow && (
          <Box>
            <Box className={s.attrHeader}>
              <Box className={cx(light.iconWrap, light.iconWrapSecondary)}>
                <BusinessOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary }} />
              </Box>
              <Box>
                <Typography className={s.attrTitle}>{pendingRow.name}</Typography>
                <Typography className={s.attrSubtitle}>
                  {[pendingRow.identifier, pendingRow.addressPostCode, pendingRow.addressCity].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
            </Box>

            <LightAttributesForm
              ref={attrFormRef}
              attributes={accountAttributes}
            />

            <Box className={s.attrActions}>
              <button
                className={s.backBtn}
                onClick={() => { setAttributesOpen(false); setPendingRow(null); }}
              >
                <ArrowBackIcon style={{ fontSize: "16px" }} />
                {i18next.t("back")}
              </button>
              <button
                className={s.continueBtn}
                onClick={() => {
                  if (!attrFormRef.current?.validate()) return;
                  const attrs = attrFormRef.current.getValues();
                  setAttributesOpen(false);
                  selectPending(pendingRow, attrs);
                  setPendingRow(null);
                }}
              >
                {i18next.t("continue")} →
              </button>
            </Box>
          </Box>
        )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
